/**
 * ErrorBoundary.
 *
 * A render crash on the Billing screen must not take the Outage screen with it, and it
 * must never leave a blank page in front of somebody at 2 AM. Two guarantees here:
 *
 *   1. The fallback tells the truth, including the fact the phone line is unaffected —
 *      the console going down is not the product going down, and an operator who does not
 *      know that will start waking people up.
 *   2. "Reload this screen" resets the boundary in place; "Reload the console" is the
 *      bigger hammer, offered second.
 */
import { Component } from 'react';
import type { ErrorInfo, ReactElement, ReactNode } from 'react';
import { Icon } from './Icon.tsx';

type Props = {
  children: ReactNode;
  /** Names the screen in the message: "The Billing screen stopped responding." */
  screen?: string;
  /** Rendered instead of the default fallback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
};

type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // No telemetry endpoint yet, and inventing one silently would be a surprise. Until
    // there is one, the console log is where a support call starts.
    console.error('[nightdesk] render error', error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="nd-empty nd-errorstate" role="alert">
        <span className="nd-empty-icon">
          <Icon name="alert-triangle" size={19} strokeWidth={1.3} />
        </span>
        <div className="nd-empty-title">
          {this.props.screen ? `The ${this.props.screen} screen stopped responding` : 'This screen stopped responding'}
        </div>
        <div className="nd-empty-body">
          Something in the console failed to draw. The phone line is unaffected — calls are still being answered and
          nothing has been lost. Reload this screen to try again.
        </div>
        <pre className="nd-mono nd-dim" style={{ margin: 0, whiteSpace: 'pre-wrap', maxWidth: '46ch' }}>
          {error.message}
        </pre>
        <div className="nd-empty-action">
          <button type="button" className="nd-btn" data-variant="primary" onClick={this.reset}>
            <Icon name="refresh" size={13} />
            Reload this screen
          </button>
          <button type="button" className="nd-btn" onClick={() => window.location.reload()}>
            Reload the console
          </button>
        </div>
      </div>
    );
  }
}

/** Route-level wrapper so a crash is scoped to one screen. */
export function ScreenBoundary({ screen, children }: { screen: string; children: ReactNode }): ReactElement {
  return <ErrorBoundary screen={screen}>{children}</ErrorBoundary>;
}

export default ErrorBoundary;
